/* iter55z-p — GuidedTour: first-run + on-demand contextual walkthroughs.
 *
 * Two entry points:
 *   1. AUTO first-run tour  — 4-step feature walkthrough on very first
 *      login (Search → Bookmarks → Notes → Export). Gated by
 *      localStorage["pw:tour-completed-v1"].
 *   2. CONTEXTUAL tours     — any component can call
 *          startContextualTour('character-bible-open', [ {anchor,title,body}, ... ])
 *      to spotlight ANY set of `data-testid` anchors. Each tour ID is
 *      shown at most once per browser (localStorage["pw:tour-<id>-done"]).
 *
 * Same visual layer (SVG spotlight + tooltip card + Skip/Back/Next) —
 * the engine just accepts a step array via a custom event.
 */
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ArrowLeft, ArrowRight, Check, Sparkles } from 'lucide-react';

const DONE_KEY_MAIN = 'pw:tour-completed-v1';
const doneKeyFor = (id) => `pw:tour-${id}-done`;

export function markTourDone(id)  {
  try { localStorage.setItem(id ? doneKeyFor(id) : DONE_KEY_MAIN, String(Date.now())); } catch {}
}
export function resetTour(id) {
  try { localStorage.removeItem(id ? doneKeyFor(id) : DONE_KEY_MAIN); } catch {}
  if (!id) window.dispatchEvent(new Event('pw:tour-restart'));
}
export function hasSeenTour(id) {
  try { return !!localStorage.getItem(id ? doneKeyFor(id) : DONE_KEY_MAIN); } catch { return false; }
}

/** Public API: kick off a contextual tour on demand.
 *  Skips silently if the tour was already completed for this browser.
 *  Usage:
 *    startContextualTour('character-bible-open', [
 *      { anchor: 'character-bible-selector', title: 'Pick a character',
 *        body:   'Search or scroll to switch which character you\'re editing.' },
 *      { anchor: 'character-bible-add-btn',  title: 'Add someone new',
 *        body:   'Add a new character to your project roster.' },
 *    ]);
 */
export function startContextualTour(id, steps, opts = {}) {
  if (!id || !Array.isArray(steps) || steps.length === 0) return;
  if (!opts.force && hasSeenTour(id)) return;
  window.dispatchEvent(new CustomEvent('pw:tour-start', {
    detail: { id, steps },
  }));
}

function useMainSteps(t) {
  return [
    { id: 'search',    anchor: 'editor-search-btn',
      title: t('tour.searchTitle'),
      body:  t('tour.searchBody') },
    { id: 'bookmarks', anchor: 'editor-bookmarks-btn',
      title: t('tour.bookmarksTitle'),
      body:  t('tour.bookmarksBody') },
    { id: 'notes',     anchor: 'editor-suggestions-btn',
      title: t('tour.notesTitle'),
      body:  t('tour.notesBody') },
    { id: 'export',    anchor: 'menu-export-trigger',
      title: t('tour.exportTitle'),
      body:  t('tour.exportBody') },
  ];
}

const TIP_W = 320;

function computeRects(anchorEl) {
  const rect     = anchorEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - 24;
  const tipH_est   = 170;
  const above      = spaceBelow < tipH_est && rect.top > tipH_est;
  const tipY       = above ? rect.top - tipH_est - 12 : rect.bottom + 12;
  let   tipX       = rect.left + rect.width / 2 - TIP_W / 2;
  tipX = Math.min(window.innerWidth - TIP_W - 16, Math.max(16, tipX));
  // spotlight kept for arrow-direction calc (no SVG drawn from it)
  const spotlight  = { x: rect.left, y: rect.top, w: rect.width, h: rect.height };
  return { spotlight, tip: { x: tipX, y: tipY } };
}

export default function GuidedTour() {
  const { t } = useTranslation();
  const MAIN_STEPS = useMainSteps(t);
  const [active, setActive] = useState(false);
  const [tourId, setTourId] = useState(null);          // null = main first-run tour
  const [steps, setSteps]   = useState(MAIN_STEPS);
  const [idx, setIdx]       = useState(0);
  const [rects, setRects]   = useState(null);
  const [missing, setMissing] = useState(false);

  // First-run auto-start. iter-bookkeeper-tour · Sep-2026 — the Admin
  // console also mounts this engine (to power the Bookkeeping panel's
  // contextual tour), but its MAIN_STEPS anchors only exist on the
  // author-facing /app surface, so skip the auto-run there entirely.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) return undefined;
    if (hasSeenTour()) return undefined;
    const t = setTimeout(() => {
      setTourId(null); setSteps(MAIN_STEPS); setIdx(0); setActive(true);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  // Manual restart of the main tour.
  useEffect(() => {
    const h = () => { setTourId(null); setSteps(MAIN_STEPS); setIdx(0); setActive(true); };
    window.addEventListener('pw:tour-restart', h);
    return () => window.removeEventListener('pw:tour-restart', h);
  }, []);

  // On-demand contextual tour.
  useEffect(() => {
    const h = (e) => {
      const { id, steps: s } = e.detail || {};
      if (!id || !Array.isArray(s) || s.length === 0) return;
      setTourId(id); setSteps(s); setIdx(0); setActive(true);
    };
    window.addEventListener('pw:tour-start', h);
    return () => window.removeEventListener('pw:tour-start', h);
  }, []);

  const step = steps[idx];

  const measure = useCallback(() => {
    if (!active || !step) return;
    const el = document.querySelector(`[data-testid="${step.anchor}"]`);
    if (!el) { setMissing(true); return; }
    setMissing(false);
    setRects(computeRects(el));
  }, [active, step]);

  useLayoutEffect(() => {
    if (!active) return undefined;
    measure();
    const iv = setInterval(measure, 400);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearInterval(iv);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, measure]);

  const dismiss = useCallback(() => {
    markTourDone(tourId);
    setActive(false);
  }, [tourId]);
  const next   = () => { if (idx === steps.length - 1) dismiss(); else setIdx(idx + 1); };
  const back   = () => { if (idx > 0) setIdx(idx - 1); };

  /* iter55f57 — Any click outside the tooltip card dismisses the tour.
   * Prior behaviour greyed the whole page out and blocked interaction
   * until the user hit Skip / Finish. The user asked for the tour to
   * behave like a lightweight guide — click anywhere else and it goes
   * away, the app is never non-interactive. Uses `pointerdown` on the
   * document (capture phase) so we catch the click before it fires
   * side-effects on the underlying element. */
  useEffect(() => {
    if (!active) return undefined;
    const onDown = (e) => {
      const tip = document.querySelector(`[data-testid^="guided-tour-tip-"]`);
      if (tip && tip.contains(e.target)) return;  // inside the card → keep tour
      // iter-process-fix2 · Feb-2026 — The "anchor missing" waiting toast
      // (Next/X buttons) is its own fixed element outside the tip card,
      // so it was being swallowed by this same capture-phase dismiss —
      // clicking its Next button closed the whole tour before the
      // button's own onClick ever ran. Whitelist it too.
      const toast = document.querySelector(`[data-testid="tour-waiting-toast"]`);
      if (toast && toast.contains(e.target)) return;
      // iter-process-fix · Feb-2026 — Several tours explicitly instruct
      // "click this icon" (e.g. The Process step 1). Clicking the exact
      // control being taught should not also kill the tour — only a
      // click elsewhere on the page counts as "dismiss".
      const anchorEl = step && document.querySelector(`[data-testid="${step.anchor}"]`);
      if (anchorEl && anchorEl.contains(e.target)) return;
      dismiss();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [active, dismiss, step]);

  // ── Highlight ring: injected directly onto the anchor element ──────────
  // No overlay, no SVG scrim — the anchor element itself gets a pulsing
  // gold ring via a CSS class. Everything behind stays fully visible and
  // interactive. The class is cleaned up on step change, dismiss, or unmount.
  useEffect(() => {
    if (!active || !step) return undefined;
    const el = document.querySelector(`[data-testid="${step.anchor}"]`);
    if (!el) return undefined;
    el.classList.add('guided-tour-active-anchor');
    return () => el.classList.remove('guided-tour-active-anchor');
  }, [active, step]);

  // Clean up any leftover ring on full dismiss.
  useEffect(() => {
    if (active) return undefined;
    document.querySelectorAll('.guided-tour-active-anchor').forEach((el) =>
      el.classList.remove('guided-tour-active-anchor'),
    );
  }, [active]);

  if (!active) return null;

  if (missing || !rects) {
    const isLast = idx === steps.length - 1;
    return (
      <>
        <div
          className="fixed bottom-4 right-4 z-[80] flex items-center gap-2 rounded-md border border-primary/40 bg-card px-3 py-2 shadow-lg"
          data-testid="tour-waiting-toast"
        >
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span className="text-[11.5px]">
            {t('tour.waitingForAnchor')} · {t('tour.stepOf', { current: idx + 1, total: steps.length })}
            {step?.title ? ` · ${step.title}` : ''}
          </span>
          {!isLast && (
            <button onClick={next} data-testid="tour-waiting-next" className="rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
              {t('tour.next')}
            </button>
          )}
          <button onClick={dismiss} data-testid="tour-waiting-dismiss" className="rounded-sm p-1 hover:bg-secondary" aria-label={t('tour.skipAria')}>
            <X className="h-3 w-3" />
          </button>
        </div>
        <TourStyles />
      </>
    );
  }

  const { tip } = rects;
  const total   = steps.length;
  // Arrow direction: if the tip is BELOW the anchor, arrow points up; otherwise down.
  const arrowUp = tip.y > (rects.spotlight.y + rects.spotlight.h);

  return (
    <>
      {/* Tooltip card — no overlay wrapper, no scrim, no SVG.
          The highlighted element's ring lives on the DOM node itself via
          the .guided-tour-active-anchor CSS class injected above. */}
      <div
        data-testid={`guided-tour-tip-${step.id || idx}`}
        data-tour-id={tourId || 'main'}
        className="guided-tour-tip pointer-events-auto fixed rounded-lg border border-amber-400/60 bg-card p-3 shadow-2xl"
        style={{ left: tip.x, top: tip.y, width: TIP_W, zIndex: 9000 }}
      >
        {/* Arrow pointing at the highlighted element */}
        <div
          className={`absolute left-1/2 -translate-x-1/2 h-0 w-0 border-x-[7px] border-x-transparent
            ${arrowUp
              ? 'bottom-full border-b-[8px] border-b-amber-400/60'
              : 'top-full  border-t-[8px] border-t-amber-400/60'}`}
          aria-hidden
        />

        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-amber-500">
          <Sparkles className="h-3 w-3" />
          {tourId ? t('tour.featureTour') : t('tour.quickTour')} · {t('tour.stepOf', { current: idx + 1, total })}
        </div>
        <div className="mt-1 text-[13.5px] font-semibold text-foreground" data-testid="guided-tour-title">
          {step.title}
        </div>
        {step.videoUrl && (
          <video
            key={step.videoUrl}
            src={step.videoUrl}
            autoPlay muted loop playsInline
            data-testid={`guided-tour-video-${step.id || idx}`}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="mt-2 h-32 w-full rounded-md border border-primary/40 bg-black object-cover"
          />
        )}
        <p className="mt-1 text-[12.5px] leading-relaxed text-foreground/85">
          {step.body}
        </p>
        <div className="mt-3 flex items-center gap-1.5">
          <button
            type="button"
            onClick={dismiss}
            className="rounded-sm px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
            data-testid="guided-tour-skip"
          >
            {t('tour.skipTour')}
          </button>
          <div className="ml-auto flex items-center gap-1">
            {idx > 0 && (
              <button
                type="button"
                onClick={back}
                className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px]"
                data-testid="guided-tour-back"
              >
                <ArrowLeft className="h-3 w-3" /> {t('tour.back')}
              </button>
            )}
            <button
              type="button"
              onClick={next}
              className="inline-flex items-center gap-1 rounded-sm bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-amber-600"
              data-testid="guided-tour-next"
            >
              {idx === total - 1
                ? (<><Check className="h-3 w-3" /> {t('tour.finish')}</>)
                : (<>{t('tour.next')} <ArrowRight className="h-3 w-3" /></>)}
            </button>
          </div>
        </div>
        {idx === 0 && (
          <div className="mt-2 text-center text-[10.5px] text-muted-foreground tour-escape-hint" data-testid="guided-tour-escape-hint">
            <span aria-hidden>☝</span> {t('tour.escapeHint')}
          </div>
        )}
      </div>
      <TourStyles />
    </>
  );
}

function TourStyles() {
  return (
    <style>{`
      /* Pulsing gold ring injected directly onto the anchor element.
         No overlay, no scrim — the rest of the UI stays fully visible. */
      .guided-tour-active-anchor {
        outline: 2px solid rgba(251,191,36,0.95) !important;
        outline-offset: 4px;
        border-radius: 6px;
        position: relative;
        z-index: 8999;
        animation: tour-anchor-pulse 1.8s ease-in-out infinite;
      }
      @keyframes tour-anchor-pulse {
        0%,100% { box-shadow: 0 0 0 0   rgba(251,191,36,0.45); outline-color: rgba(251,191,36,0.95); }
        50%     { box-shadow: 0 0 0 6px rgba(251,191,36,0);    outline-color: rgba(251,191,36,1);    }
      }
      /* Tooltip entrance */
      .guided-tour-tip {
        animation: tour-tip-in 0.18s ease-out both;
      }
      @keyframes tour-tip-in {
        from { opacity: 0; transform: translateY(4px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      /* Escape hint */
      @keyframes tour-escape-fade {
        from { opacity: 0; transform: translateY(2px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .tour-escape-hint { animation: tour-escape-fade 1s ease-out both; }
    `}</style>
  );
}
